import { Fragment, useMemo } from "react";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Skeleton from "@mui/material/Skeleton";
import { SortableHeaderCell } from "./PivotGroupedTable";
import {
  VISIT_METRIC_COLUMNS,
  fmtQty,
  fmtMoney,
  THAI_WEEKDAY_SHORT,
} from "./pivotFormat";
import { PV, WEEKDAY_COLORS } from "./pivotTokens";
import {
  Metrics,
  VisitAttendanceTotals,
  VisitGrid,
  VisitGroup,
  VisitRow,
} from "../../types/pivot.type";

type VisitTotals = Metrics & VisitAttendanceTotals;

const HEAD_CELL = {
  bgcolor: PV.head,
  color: PV.tx2,
  fontWeight: 500,
  fontSize: 13,
  padding: "12px 16px",
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${PV.line}`,
  position: "sticky",
  top: 0,
  zIndex: 2,
} as const;

const BODY_CELL = {
  padding: "13px 16px",
  borderBottom: `1px solid ${PV.line2}`,
  verticalAlign: "middle",
  fontSize: 13.5,
} as const;

/** Gap tiers mirror the gap-bucket thresholds so the column and the tiles agree. */
const gapColor = (days: number) =>
  days > 15 ? PV.waste : days > 7 ? PV.leftover : days > 3 ? PV.indigo : PV.tx;

const DayCell = ({ cell, maxSales }: { cell?: VisitRow["days"][number]; maxSales: number }) => {
  const base = { width: 15, height: 15, borderRadius: "2px", mx: "auto" } as const;

  if (!cell || cell.status === "NOT_PLANNED") {
    const weekend = cell ? [0, 6].includes(dayjs(cell.date).day()) : false;
    return (
      <Tooltip title={cell ? `${cell.date} · ไม่อยู่ในแผนรอบเข้าร้าน` : ""} arrow>
        <Box sx={{ ...base, bgcolor: weekend ? "#EDEFF5" : PV.line2 }} />
      </Tooltip>
    );
  }

  if (cell.status === "MISSED") {
    return (
      <Tooltip title={`${cell.date} · อยู่ในแผนแต่ไม่ได้เข้า`} arrow>
        <Box sx={{ ...base, bgcolor: PV.wasteTint, boxShadow: `inset 0 0 0 1.5px ${PV.waste}` }} />
      </Tooltip>
    );
  }

  const unplanned = cell.status === "VISITED_UNPLANNED";
  const weekdayColor = WEEKDAY_COLORS[dayjs(cell.date).day()];
  // Shade each cell against its own row's best day, so a quiet store still reads.
  const opacity = maxSales > 0 ? 0.42 + 0.58 * ((cell.sales_amount ?? 0) / maxSales) : 1;

  return (
    <Tooltip
      arrow
      title={`${cell.date} · ${unplanned ? "เข้าร้าน (นอกแผน)" : "เข้าร้าน"} ${fmtMoney(
        cell.sales_amount,
      )} (${fmtQty(cell.sold_qty)} ชิ้น)`}
    >
      <Box
        sx={{
          ...base,
          bgcolor: weekdayColor,
          opacity,
          boxShadow: unplanned ? `inset 0 0 0 1.5px ${PV.tx}` : "none",
        }}
      />
    </Tooltip>
  );
};

const GroupDayCell = ({ visited, missed }: { visited: number; missed: number }) => {
  if (!visited && !missed) return <Box sx={{ color: PV.tx3 }}>–</Box>;
  return (
    <Tooltip arrow title={`เข้า ${visited} ร้าน${missed ? ` · ไม่ได้เข้า ${missed} ร้าน` : ""}`}>
      <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
        {visited}
        {missed > 0 && (
          <Box component="span" sx={{ color: PV.waste, ml: 0.3 }}>
            +{missed}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

interface PivotVisitTableProps {
  groups: VisitGroup[];
  grandTotal: VisitTotals;
  totalRowCount: number;
  missedCount: number;
  grid: VisitGrid;
  groupLabel: string;
  unit: string;
  sortBy: string;
  sortDir: "asc" | "desc";
  onSort: (key: string) => void;
  collapsed: Set<string>;
  onToggleGroup: (key: string) => void;
  loading?: boolean;
}

export default function PivotVisitTable({
  groups,
  grandTotal,
  totalRowCount,
  missedCount,
  grid,
  groupLabel,
  unit,
  sortBy,
  sortDir,
  onSort,
  collapsed,
  onToggleGroup,
  loading,
}: PivotVisitTableProps) {
  const days = useMemo(() => {
    const out: string[] = [];
    let d = dayjs(grid.date_from);
    const end = dayjs(grid.date_to);
    // Guard the loop: a malformed grid (date_to before date_from) must render nothing
    // rather than spin, and the API's own cap is 92 columns.
    while ((d.isBefore(end) || d.isSame(end, "day")) && out.length < 92) {
      out.push(d.format("YYYY-MM-DD"));
      d = d.add(1, "day");
    }
    return out;
  }, [grid.date_from, grid.date_to]);

  const leadCols = 4;
  const columnCount = leadCols + days.length + VISIT_METRIC_COLUMNS.length;

  return (
    <Box sx={{ overflowX: "auto", maxHeight: 640, overflowY: "auto" }}>
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
        <Box component="thead">
          <Box component="tr">
            <Box
              component="th"
              sx={{ ...HEAD_CELL, textAlign: "left", minWidth: 250, pl: "20px" }}
            >
              ร้านค้า
            </Box>
            <SortableHeaderCell
              label="เข้าจริง / ตามแผน"
              align="center"
              sortKey="visited_count"
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={onSort}
              sx={{ bgcolor: PV.head, minWidth: 108 }}
            />
            <SortableHeaderCell
              label="รอบที่พลาด"
              align="center"
              sortKey="missed_count"
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={onSort}
              sx={{ minWidth: 96 }}
            />
            <SortableHeaderCell
              label="ห่างจากรอบล่าสุด (วัน)"
              align="center"
              sortKey="days_since_last_visit"
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={onSort}
              sx={{ minWidth: 108 }}
            />
            {days.map((d) => {
              const wd = dayjs(d).day();
              return (
                <Box
                  component="th"
                  key={d}
                  sx={{
                    ...HEAD_CELL,
                    width: 30,
                    minWidth: 30,
                    padding: "6px 4px",
                    textAlign: "center",
                    borderLeft: wd === 1 ? `1px solid ${PV.line2}` : undefined,
                  }}
                >
                  <Box sx={{ fontSize: 10, color: WEEKDAY_COLORS[wd], lineHeight: 1.3 }}>
                    {THAI_WEEKDAY_SHORT[wd]}
                  </Box>
                  <Box sx={{ fontSize: 11.5, fontWeight: 600, color: PV.tx }}>{dayjs(d).date()}</Box>
                </Box>
              );
            })}
            {VISIT_METRIC_COLUMNS.map((c) => (
              <SortableHeaderCell
                key={c.key}
                label={c.label}
                align={c.align}
                sortKey={c.key}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={onSort}
              />
            ))}
          </Box>
        </Box>

        <Box component="tbody">
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <Box component="tr" key={i}>
                {Array.from({ length: leadCols + VISIT_METRIC_COLUMNS.length }).map((__, j) => (
                  <Box component="td" key={j} sx={BODY_CELL}>
                    <Skeleton variant="text" />
                  </Box>
                ))}
              </Box>
            ))}

          {!loading && !groups.length && (
            <Box component="tr">
              <Box component="td" colSpan={columnCount} sx={{ ...BODY_CELL, borderBottom: 0 }}>
                <Box sx={{ py: 5, textAlign: "center", color: PV.tx3 }}>
                  <Typography sx={{ fontWeight: 600, color: PV.tx2, mb: 0.5 }}>
                    ไม่มีข้อมูลตามตัวกรอง
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    ลองขยายช่วงวันที่ ล้างคำค้น หรือเปลี่ยนขอบเขต
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {!loading &&
            groups.map((g) => {
              const isCollapsed = collapsed.has(g.group.key);
              return (
                <Fragment key={g.group.key}>
                  <Box
                    component="tr"
                    onClick={() => onToggleGroup(g.group.key)}
                    sx={{
                      cursor: "pointer",
                      "& > td": {
                        bgcolor: PV.head,
                        borderTop: `1px solid ${PV.line}`,
                        borderBottom: `1px solid ${PV.line2}`,
                        padding: "11px 8px",
                        whiteSpace: "nowrap",
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                        textAlign: "center",
                      },
                      "&:hover > td": { bgcolor: "#EDEFF6" },
                    }}
                  >
                    <Box component="td" colSpan={leadCols} sx={{ textAlign: "left !important", pl: "20px" }}>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          width: 15,
                          color: PV.tx3,
                          fontSize: 10,
                          transform: isCollapsed ? "rotate(-90deg)" : "none",
                          transition: "transform .12s",
                        }}
                      >
                        ▾
                      </Box>
                      <Box component="span" sx={{ fontSize: 13.5, fontWeight: 600, color: PV.tx }}>
                        {g.group.label ?? g.group.key}
                      </Box>
                      <Box component="span" sx={{ fontSize: 11.5, color: PV.tx3, ml: 1.1, fontWeight: 400 }}>
                        {fmtQty(g.row_count)} {unit}
                        {g.totals.missed_count > 0 && (
                          <Box component="span" sx={{ color: PV.waste, ml: 0.5 }}>
                            · ไม่ได้เข้า {fmtQty(g.totals.missed_count)} รอบ
                          </Box>
                        )}
                      </Box>
                    </Box>
                    {days.map((d) => (
                      <Box component="td" key={d}>
                        <GroupDayCell
                          visited={g.totals.visited_by_date?.[d] ?? 0}
                          missed={g.totals.missed_by_date?.[d] ?? 0}
                        />
                      </Box>
                    ))}
                    {VISIT_METRIC_COLUMNS.map((c) => (
                      <Box component="td" key={c.key} sx={{ textAlign: `${c.align} !important` }}>
                        {c.render(g.totals[c.key] as number | null)}
                      </Box>
                    ))}
                  </Box>

                  {!isCollapsed &&
                    g.rows.map((row) => {
                      const byDate = new Map(row.days.map((d) => [d.date, d]));
                      return (
                        <Box
                          component="tr"
                          key={row.customer_code}
                          sx={{ "&:hover > td": { bgcolor: PV.rowHover } }}
                        >
                          <Box component="td" sx={{ ...BODY_CELL, pl: "20px" }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.35 }}>
                              {row.customer_name}
                            </Typography>
                            <Typography sx={{ fontSize: 11.5, color: PV.tx3 }}>
                              {row.customer_code} · {row.route_name ?? row.route_code}
                            </Typography>
                          </Box>
                          <Box
                            component="td"
                            sx={{ ...BODY_CELL, textAlign: "center", fontVariantNumeric: "tabular-nums" }}
                          >
                            <Box
                              component="span"
                              sx={{ color: row.visited_count < row.planned_count ? PV.leftover : PV.tx }}
                            >
                              {fmtQty(row.visited_count)}
                              <Box component="span" sx={{ fontSize: 11.5, color: PV.tx3 }}>
                                /{fmtQty(row.planned_count)}
                              </Box>
                            </Box>
                          </Box>
                          <Box
                            component="td"
                            sx={{ ...BODY_CELL, textAlign: "center", fontVariantNumeric: "tabular-nums" }}
                          >
                            {row.missed_count ? (
                              <Box component="span" sx={{ color: PV.waste }}>
                                {fmtQty(row.missed_count)}
                              </Box>
                            ) : (
                              <Box component="span" sx={{ color: PV.tx3 }}>
                                –
                              </Box>
                            )}
                          </Box>
                          <Box
                            component="td"
                            sx={{ ...BODY_CELL, textAlign: "center", fontVariantNumeric: "tabular-nums" }}
                          >
                            <Box
                              component="span"
                              sx={{ color: gapColor(row.days_since_last_visit), fontWeight: 600 }}
                            >
                              {row.days_since_last_visit_is_capped
                                ? "200+"
                                : fmtQty(row.days_since_last_visit)}
                            </Box>
                          </Box>
                          {days.map((d) => (
                            <Box
                              component="td"
                              key={d}
                              sx={{ ...BODY_CELL, padding: "6px 4px", textAlign: "center" }}
                            >
                              <DayCell cell={byDate.get(d)} maxSales={row.max_daily_sales_amount} />
                            </Box>
                          ))}
                          {VISIT_METRIC_COLUMNS.map((c) => (
                            <Box
                              component="td"
                              key={c.key}
                              sx={{ ...BODY_CELL, textAlign: c.align, fontVariantNumeric: "tabular-nums" }}
                            >
                              {c.render(row.metrics[c.key] as number | null)}
                            </Box>
                          ))}
                        </Box>
                      );
                    })}

                  {!isCollapsed && g.truncated_row_count > 0 && (
                    <Box component="tr">
                      <Box
                        component="td"
                        colSpan={columnCount}
                        sx={{ ...BODY_CELL, pl: "20px", fontSize: 12.5, color: PV.tx3, fontStyle: "italic" }}
                      >
                        …อีก {fmtQty(g.truncated_row_count)} {unit}ในกลุ่มนี้ที่ไม่ได้แสดงบนจอ ·
                        ดาวน์โหลด CSV เพื่อดูครบ
                      </Box>
                    </Box>
                  )}
                </Fragment>
              );
            })}
        </Box>

        {!loading && groups.length > 0 && (
          <Box component="tfoot">
            <Box
              component="tr"
              sx={{
                "& > td": {
                  bgcolor: PV.head,
                  fontWeight: 600,
                  fontSize: 13,
                  borderTop: `1px solid ${PV.line}`,
                  padding: "13px 8px",
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                  textAlign: "center",
                  position: "sticky",
                  bottom: 0,
                },
              }}
            >
              <Box component="td" colSpan={leadCols} sx={{ textAlign: "left !important", pl: "20px" }}>
                รวมทั้งหมด · {fmtQty(totalRowCount)} {unit}
                {missedCount > 0 && ` · ไม่ได้เข้า ${fmtQty(missedCount)} รอบ`}
                {groups.length > 0 && ` · ${fmtQty(groups.length)} ${groupLabel}`}
              </Box>
              {days.map((d) => (
                <Box component="td" key={d}>
                  <GroupDayCell
                    visited={grandTotal.visited_by_date?.[d] ?? 0}
                    missed={grandTotal.missed_by_date?.[d] ?? 0}
                  />
                </Box>
              ))}
              {VISIT_METRIC_COLUMNS.map((c) => (
                <Box component="td" key={c.key} sx={{ textAlign: `${c.align} !important` }}>
                  {c.render(grandTotal[c.key] as number | null)}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
